import React, {useMemo, useState} from "react";
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	Stack,
	Typography
} from "@mui/material";
import {useAsync, useAsyncCallback} from "react-async-hook";
import {BoxAvailability, useSiteApi} from "../data/sites";
import {useCenterApi} from "../data/center";
import {isBlank, notBlank} from "../utils";
import {useLocalization} from "../i18n";
import {TextField, useFlag} from "@variocube/app-ui";
import {QrcodeScanner} from "./qrcode-scanner";

import Location from "../assets/location_r52x.svg";

interface LocationVerificationProps {
	bookingId: string;
	onCancel: () => void,
	onVerified: (cubeId: string, availability: BoxAvailability) => void;
	cubeDescription?: string;
	qrcodeReaderEnabled?: boolean;
	requiredCubeId?: string;
	automaticallySelectCubeId?: string;
}

export function LocationVerification(props: LocationVerificationProps) {
	const {
		bookingId,
		onCancel,
		onVerified,
		cubeDescription,
		qrcodeReaderEnabled,
		requiredCubeId,
		automaticallySelectCubeId
	} = props;
	const {t} = useLocalization();
	const {getCodeShape, getCubeByCode} = useCenterApi();
	const {verifyBookingLocation} = useSiteApi();

	const [showReader, setShowReader, clearShowReader] = useFlag(false);
	const [scannedValue, setScannedValue] = useState('');
	const [code, setCode] = useState('');

	const {result: codeShape} = useAsync(async () =>
		isBlank(automaticallySelectCubeId)
			? await getCodeShape()
			: undefined
		, [automaticallySelectCubeId]);

	const {result: cube, error: cubeError, loading: cubeLoading, execute: checkCube, reset: resetCube} = useAsyncCallback(getCubeByCode);
	const {error: locationError, loading: locationLoading, execute: verifyLocation, reset: resetLocation} = useAsyncCallback(verifyBookingLocation);

	const verificationError = useMemo(() =>
			cubeError !== undefined
			|| locationError !== undefined
			|| (notBlank(requiredCubeId) && notBlank(scannedValue) && requiredCubeId !== scannedValue)
			|| (notBlank(requiredCubeId) && notBlank(cube?.hostname) && requiredCubeId !== cube?.hostname)
		, [cubeError, locationError, requiredCubeId, scannedValue, cube]);

	function handleCodeScanned(value: string) {
		setScannedValue(value);
		console.info('Scanned QR-Code', value);
		clearShowReader();
		handleVerifyLocation(value);
	}

	async function handleCodeTyped(value: string) {
		const typed = value.replace(/\D/g, '');
		if (codeShape && typed.length >= codeShape.length) {
			try {
				const cube = await checkCube(typed);
				if (cube !== undefined && (isBlank(requiredCubeId) || cube?.hostname === requiredCubeId)) {
					handleVerifyLocation(cube.hostname);
				}
			} catch (err) {

			}
		}
		setCode(typed);
	}

	function handleVerifyLocation(cubeId: string) {
		verifyLocation(bookingId, cubeId)
			.then(availability => onVerified(cubeId, availability))
			.catch(console.error);
	}

	function reset() {
		setScannedValue('');
		setCode('');
		resetCube();
		resetLocation();
	}

	return (
		<>
			<Stack
				direction="column"
				alignItems="center"
				gap={2}
			>
				<Location style={{ width: '150px', height: 'auto' }} />
				{locationLoading && <CircularProgress />}
				{!locationLoading && (
					<>
						<Typography>
							{qrcodeReaderEnabled
								? t('location.verification.message')
								: t('location.verification.messageWithoutQrcode')
							}
						</Typography>
						{notBlank(cubeDescription) && <Typography variant="subtitle1">{cubeDescription}</Typography>}
						{qrcodeReaderEnabled && (
							<>
								<Button
									variant="contained"
									color="primary"
									onClick={setShowReader}
								>
									{t('location.scanQrCode')}
								</Button>
								<Typography variant="subtitle1">{t('location.or')}</Typography>
								<QrcodeScanner
									open={showReader}
									onScanned={handleCodeScanned}
									onClose={clearShowReader}
								/>
							</>
						)}
						<TextField
							size="small"
							label={t('location.useLocationCode')}
							value={code}
							type="number"
							onChange={handleCodeTyped}
							disabled={cubeLoading}
						/>
						<Button
							variant="contained"
							color="inherit"
							disableElevation
							onClick={onCancel}
						>
							{t('actions.cancel')}
						</Button>
					</>
				)}
			</Stack>
			<Dialog open={verificationError} onClose={reset}>
				<DialogContent>
					<Typography>{t('location.verification.error')}</Typography>
					{cubeDescription && <Typography>{t('location.verification.hint')} {cubeDescription}</Typography>}
				</DialogContent>
				<DialogActions>
					<Button
						size="small"
						variant="contained"
						onClick={reset}
						disableElevation
					>
						{t('actions.close')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}
